import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyEVDCDeviceFormData,
    mapEvdcDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { EVDCDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { CONTINUE_DRAFT_CONFIG } from "../../../swrConfig"
import { ContinueEVDCDraftResponse } from "../../../types/devices/lct/publishEvdcDeviceTypes"
import { CONTINUE_LCT_DEVICE_DRAFT } from "../../../urls/private"

interface IUseContinueEVDCDeviceDraftOutput {
    data: EVDCDeviceFormData
    setData: Dispatch<SetStateAction<EVDCDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    attachmentUUID: string | null
    apiResSystemRef: string | null
    error?: AppError
}

const continueEVDCDeviceDraftMapper = getTsIoMapper(
    ContinueEVDCDraftResponse,
    "continueEVDCDeviceDraftMapper"
)

const useContinueEVDCDeviceDraft = (
    deviceRef: string
): IUseContinueEVDCDeviceDraftOutput => {
    const [data, setData] = useState<EVDCDeviceFormData>(
        getEmptyEVDCDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const attachmentUUID = useRef<string | null>(null)
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        CONTINUE_LCT_DEVICE_DRAFT(deviceRef, LCTOnlyDatasets.EVDC),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = continueEVDCDeviceDraftMapper.map(response)
                attachmentUUID.current = mappedRes.Attachment_UUID
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapEvdcDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError("Failed to continue draft version")
            } finally {
                setIsLoading(false)
            }
        },
        CONTINUE_DRAFT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        attachmentUUID: attachmentUUID.current,
        apiResSystemRef: apiResSystemRef.current,
        error,
    }
}

export default useContinueEVDCDeviceDraft
